import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import React, { ReactNode, useState } from "react";
import MainLayout from "../../../layouts/main/main_layout";
import { ArrowBack, RedeemOutlined } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "../../../components/loading/loading";
import { PointsStatement } from "../model/points_statement";
import RedeemPopup from "../component/redeem_popup/redeem_popup";
import { useQuery } from "react-query";
import {
  getRedemptionHistory,
  getRewardsListById,
  getRewardsStatement,
  redemption,
  updateRedemptionComments,
} from "../../../services/rewards/rewards";
import { RewardsDetailsModel } from "../model/reward_details_model";
import { RedemptionHistory } from "../model/redemption_history";
import RedemptionHistoryComp from "../component/redemption_history/redemption_history";
import { useFeedback } from "../../../providers/feedback/feeedback";
import RewardStatement from "../../reports/reward_statement/reward_statement";
import { queryClient } from "../../../providers/query/query";
import useUser from "../../../providers/user/user_provider";
import { TableFooterPagination } from "../../users/components/table_toolkit/table_toolkit";
import { black, purple, purpleLight } from "../../../theme/colors";

const ROWS_PER_PAGE_OPTIONS = [8, 25, 50];

// A small bold, uppercase divider label — breaks the page into scannable
// groups instead of one long flat list of fields. Same pattern as
// src/views/cms/news/add_edit_news.tsx's SectionHeader.
function SectionHeader({ children, mt = 4 }: { children: ReactNode; mt?: number }) {
  return (
    <Box mt={mt} mb={1.5}>
      <Typography variant="overline" fontWeight={700} color={purple} sx={{ letterSpacing: 0.6 }}>
        {children}
      </Typography>
      <Divider sx={{ mt: 0.5 }} />
    </Box>
  );
}

export default function RewardsDetails() {
  const [currentTab, setCurrentTab] = React.useState(0);
  const [openRewardPopup, setOpenRewardPopup] = useState(false);
  const navigate = useNavigate();
  const { showSnackbar } = useFeedback();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(8);
  const [statementPage, setStatementPage] = useState(0);
  const [statementPageSize, setStatementPageSize] = useState(8);
  const { id } = useParams();
  const { hasPermission } = useUser();

  const handleChange = (event: React.SyntheticEvent, currentTabVal: number) => {
    setCurrentTab(currentTabVal);
  };

  const { data: rewardsList, isLoading } = useQuery<RewardsDetailsModel>(
    ["reward-details"],
    async () => {
      let response = await getRewardsListById({
        userId: +id!,
      });
      return response?.data?.data;
    }
  );

  const { data: rewardStatement } = useQuery(
    ["reward-statement", page, pageSize, id],
    async () => {
      let response = await getRewardsStatement({
        userId: +id!,
        page: page + 1,
        size: pageSize,
      });
      return response?.data?.data || [];
    }
  );

  function redemptionApi(comments: string, points: number) {
    redemption({ userId: +id!, comments: comments, points: points })
      .then((res) => {
        showSnackbar("Points redeemed successfully");
        queryClient.invalidateQueries({
          queryKey: ["reward-details"],
        });
        queryClient.invalidateQueries({
          queryKey: ["reward-statement"],
        });
        queryClient.invalidateQueries({
          queryKey: ["redemption-history"],
        });
      })
      .catch((e) => {
        if (e?.response?.data?.error?.message) {
          showSnackbar(e.response.data.error.message);
        } else {
          showSnackbar("Something went wrong");
        }
      });
    setOpenRewardPopup(false);
  }

  const { data: redemptionHistory } = useQuery(
    ["redemption-history", statementPage, statementPageSize, id],
    async () => {
      let response = await getRedemptionHistory({
        userId: +id!,
        page: statementPage + 1,
        size: statementPageSize,
      });
      return response?.data?.data || [];
    }
  );

  function updateRedemptionApiCall(comments: string, redemptionId: number) {
    updateRedemptionComments({ comments: comments, redemptionId: redemptionId })
      .then((res) => {
        showSnackbar("Note Added successfully");
        queryClient.invalidateQueries({
          queryKey: ["redemption-history"],
        });
      })
      .catch((e) => {
        showSnackbar("Something went wrong");
      });
  }

  function listItem({
    title,
    value,
  }: {
    title: string;
    value: any;
  }): JSX.Element {
    return (
      <Box>
        <Typography color="primary.main">{title}</Typography>
        <Typography>{value || "-"}</Typography>
      </Box>
    );
  }

  function loading() {
    return (
      <Box
        height={500}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Loading />
      </Box>
    );
  }

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleStatementChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setStatementPage(newPage);
  };

  const statementCount = rewardStatement?.total || 0;
  const statementStartRange = page * pageSize + 1;
  const statementEndRange = Math.min((page + 1) * pageSize, statementCount);
  const statementSummaryText = `Showing ${statementCount ? statementStartRange : 0} to ${statementEndRange || 0} of ${statementCount || 0} entries`;

  const historyCount = redemptionHistory?.total || 0;
  const historyStartRange = statementPage * statementPageSize + 1;
  const historyEndRange = Math.min((statementPage + 1) * statementPageSize, historyCount);
  const historySummaryText = `Showing ${historyCount ? historyStartRange : 0} to ${historyEndRange || 0} of ${historyCount || 0} entries`;

  return (
    <MainLayout>
      <Grid container spacing={3}>
        <Grid item md={8} lg={7} sm={12} xs={12}>
          <Card
            elevation={0}
            sx={{ width: "100%", borderRadius: 3, border: "1px solid #ECECEC", m: 1 }}
          >
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Box display="flex" flexDirection="row" alignItems="center">
                <IconButton onClick={() => navigate(-1)}>
                  <ArrowBack></ArrowBack>
                </IconButton>
                <Box display="flex" alignItems="center" gap={1.5} ml={0.5}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: purpleLight,
                      flexShrink: 0,
                    }}
                  >
                    <RedeemOutlined sx={{ color: purple, fontSize: 22 }} />
                  </Box>
                  <Typography variant="h6" fontWeight="600">
                    Reward Points Details
                  </Typography>
                </Box>
              </Box>
              <Box m={2}>
                {isLoading ? (
                  loading()
                ) : (
                  <Box overflow="auto" flex={1}>
                    <SectionHeader mt={1}>User Details</SectionHeader>
                    <Grid container spacing={3}>
                      <Grid item md={6}>
                        {listItem({
                          title: "UID",
                          value: rewardsList?.userId,
                        })}
                      </Grid>
                      <Grid item md={6}>
                        {listItem({
                          title: "Full Name",
                          value: rewardsList?.name,
                        })}
                      </Grid>
                      <Grid item md={6}>
                        {listItem({
                          title: "Contact Number",
                          value: rewardsList?.mobileNumber,
                        })}
                      </Grid>
                      <Grid item md={6}>
                        {listItem({
                          title: "Age",
                          value: rewardsList?.age,
                        })}
                      </Grid>
                      <Grid item md={6}>
                        {listItem({
                          title: "Gender",
                          value: rewardsList?.gender,
                        })}
                      </Grid>

                      <Grid item md={6}>
                        {listItem({
                          title: "Occupation",
                          value: rewardsList?.occupation,
                        })}
                      </Grid>

                      <Grid item md={6}>
                        {listItem({
                          title: "State",
                          value: rewardsList?.state,
                        })}
                      </Grid>
                      <Grid item md={6}>
                        {listItem({
                          title: "District",
                          value: rewardsList?.district,
                        })}
                      </Grid>
                    </Grid>

                    <SectionHeader>Reward Points</SectionHeader>
                    <Grid container spacing={3}>
                      <Grid item md={6}>
                        {listItem({
                          title: "Current reward points",
                          value: rewardsList?.currentPoints,
                        })}
                      </Grid>

                      <Grid item md={6}>
                        {listItem({
                          title: "Used reward points",
                          value: rewardsList?.redeemedPoints,
                        })}
                      </Grid>

                      <Grid item md={6}>
                        {listItem({
                          title: "Total reward points gained",
                          value: rewardsList?.totalPoints,
                        })}
                      </Grid>
                    </Grid>

                    <Tabs
                      value={currentTab}
                      aria-label="basic tabs example"
                      onChange={handleChange}
                      sx={{ mt: 3 }}
                      TabIndicatorProps={{ style: { backgroundColor: purple, height: 3, borderRadius: 3 } }}
                    >
                      <Tab
                        label="Points statement"
                        sx={{ textTransform: "capitalize", color: black }}
                      />
                      <Tab
                        label="Redemption history"
                        sx={{ textTransform: "capitalize", color: black }}
                      />
                    </Tabs>
                    <Divider />

                    {currentTab === 0 && (
                      <Box mt={3}>
                        {rewardStatement?.list?.length ? (
                          rewardStatement?.list?.map(
                            (pointHistory: PointsStatement) => {
                              return (
                                <RewardStatement pointHistory={pointHistory} />
                              );
                            }
                          )
                        ) : (
                          <Box mt={3} display={"flex"} justifyContent={"center"}>
                            <Typography>No data found</Typography>
                          </Box>
                        )}
                        <Box mt={1}>
                          <TableFooterPagination
                            count={statementCount}
                            page={page}
                            onPageChange={handleChangePage}
                            summaryText={statementSummaryText}
                            rowsPerPage={pageSize}
                            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            onRowsPerPageChange={setPageSize}
                          />
                        </Box>
                      </Box>
                    )}
                    {currentTab === 1 && (
                      <Box>
                        {redemptionHistory?.list?.length ? (
                          redemptionHistory?.list?.map(
                            (history: RedemptionHistory) => {
                              return (
                                <RedemptionHistoryComp
                                  history={history}
                                  onSubmit={(id, comments) => {
                                    updateRedemptionApiCall(
                                      comments,
                                      history.id
                                    );
                                  }}
                                />
                              );
                            }
                          )
                        ) : (
                          <Box
                            mt={3}
                            display={"flex"}
                            justifyContent={"center"}
                          >
                            <Typography>No history found</Typography>
                          </Box>
                        )}
                        <Box mt={1}>
                          <TableFooterPagination
                            count={historyCount}
                            page={statementPage}
                            onPageChange={handleStatementChangePage}
                            summaryText={historySummaryText}
                            rowsPerPage={statementPageSize}
                            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            onRowsPerPageChange={setStatementPageSize}
                          />
                        </Box>
                      </Box>
                    )}

                    <Box>
                      <Button
                        disabled={
                          !hasPermission("rewards", "update") ||
                            +rewardsList?.currentPoints! === 0
                            ? true
                            : false
                          // : false
                        }
                        variant="contained"
                        sx={{ mt: 2, minWidth: 170 }}
                        onClick={() => setOpenRewardPopup(true)}
                      >
                        Redeem
                      </Button>
                    </Box>
                    <Divider sx={{ backgroundColor: "#F5E6F5", my: 3 }} />
                    <Grid item md={12} mt={4}>
                      <Box display="flex" justifyContent="space-between">
                        <Button
                          variant="outlined"
                          sx={{
                            pl: 2,
                            pr: 2,
                            textTransform: "capitalize",
                            fontWeight: 600,
                            minWidth: 170,
                          }}
                          onClick={() => navigate(-1)}
                        >
                          Back To Rewards
                        </Button>
                      </Box>
                    </Grid>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {openRewardPopup && (
        <RedeemPopup
          open={openRewardPopup}
          onDialogClose={() => {
            setOpenRewardPopup(false);
          }}
          updateRewards={(remarks, points) => {
            redemptionApi(remarks, points);
          }}
          rewardsList={rewardsList || null}
        />
      )}
    </MainLayout>
  );
}
